import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import db from "../Firebase";
import { useAuth } from "../AuthContext";

const TEAL = "#0AADA1";
const AMBER = "#f59e0b";
const PURPLE = "#6c47ff";

function fmt(n = 0) {
    if (n >= 1000) return (n / 1000).toFixed(1) + "k";
    return n;
}

function MiniBar({ daily }) {
    const days = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        days.push(d.toISOString().split("T")[0]);
    }
    const values = days.map(d => daily?.[d]?.views || 0);
    const max = Math.max(...values, 1);
    return (
        <div style={{ display: "flex", alignItems: "flex-end", gap: "3px", height: "32px" }}>
            {values.map((v, i) => (
                <div key={i} style={{
                    flex: 1,
                    height: `${Math.max((v / max) * 100, v > 0 ? 15 : 5)}%`,
                    background: i === 6 ? TEAL : v > 0 ? "rgba(10,173,161,0.4)" : "rgba(255,255,255,0.06)",
                    borderRadius: "3px 3px 2px 2px",
                    minHeight: "2px",
                }} />
            ))}
        </div>
    );
}

export default function AllAnalyticsPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;
        const load = async () => {
            try {
                const snap = await getDocs(query(collection(db, "cards"), where("userId", "==", user.uid)));
                const cardList = snap.docs.map(d => ({ ...d.data(), _docId: d.id }));

                // Fetch analytics for all cards in parallel
                const withAnalytics = await Promise.all(
                    cardList.map(async card => {
                        const slug = card.slug;
                        const aSnap = await getDoc(doc(db, "analytics", slug));
                        const a = aSnap.exists() ? aSnap.data() : {};
                        return { ...card, analytics: a };
                    })
                );
                setCards(withAnalytics);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user]);

    // Totals across all cards
    const totals = cards.reduce((acc, c) => ({
        views:  acc.views  + (c.analytics?.total_views  || 0),
        saves:  acc.saves  + (c.analytics?.total_saves  || 0),
        wallet: acc.wallet + (c.analytics?.total_wallet || 0),
    }), { views: 0, saves: 0, wallet: 0 });

    if (user === undefined || loading) {
        return (
            <div style={s.page}>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "14px", margin: "auto" }}>Loading…</div>
            </div>
        );
    }

    if (!user) {
        return (
            <div style={s.page}>
                <div style={s.gate}>
                    <div style={s.gateIcon}>🔒</div>
                    <h2 style={s.gateTitle}>Sign in required</h2>
                    <button onClick={() => navigate("/")} style={s.gateBtn}>Go Home</button>
                </div>
            </div>
        );
    }

    return (
        <div style={s.page}>
            {/* Top bar */}
            <div style={s.topBar}>
                <button onClick={() => navigate(-1)} style={s.backBtn}>← Back</button>
                <span style={s.topLogo}>myprofilecorner</span>
            </div>

            <div style={s.body}>
                {/* Hero */}
                <div style={s.hero}>
                    <div style={s.heroChip}>Overview</div>
                    <h1 style={s.heroName}>All Cards Analytics</h1>
                    <p style={s.heroSub}>{cards.length} card{cards.length !== 1 ? "s" : ""} · all time stats</p>
                </div>

                {/* Combined totals */}
                <div style={s.totalRow}>
                    <div style={s.totalTile}>
                        <span style={s.totalIcon}>👁</span>
                        <span style={{ fontSize: "24px", fontWeight: "800", color: "#fff" }}>{fmt(totals.views)}</span>
                        <span style={{ fontSize: "11px", color: TEAL, fontWeight: "600" }}>Total Views</span>
                    </div>
                    <div style={s.totalTile}>
                        <span style={s.totalIcon}>💾</span>
                        <span style={{ fontSize: "24px", fontWeight: "800", color: "#fff" }}>{fmt(totals.saves)}</span>
                        <span style={{ fontSize: "11px", color: PURPLE, fontWeight: "600" }}>Total Saves</span>
                    </div>
                    <div style={s.totalTile}>
                        <span style={s.totalIcon}>📲</span>
                        <span style={{ fontSize: "24px", fontWeight: "800", color: "#fff" }}>{fmt(totals.wallet)}</span>
                        <span style={{ fontSize: "11px", color: AMBER, fontWeight: "600" }}>Wallet Adds</span>
                    </div>
                </div>

                {/* Card list */}
                <div style={s.sectionLabel}>Your Cards</div>

                {cards.length === 0 && (
                    <div style={s.empty}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>📭</div>
                        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "14px" }}>No cards yet. Create one to start tracking.</p>
                        <button onClick={() => navigate("/create")} style={s.gateBtn}>Create Card</button>
                    </div>
                )}

                {cards.map(card => {
                    const a = card.analytics;
                    const views  = a?.total_views  || 0;
                    const saves  = a?.total_saves  || 0;
                    const wallet = a?.total_wallet || 0;
                    const todayKey = new Date().toISOString().split("T")[0];
                    const todayViews = a?.daily?.[todayKey]?.views || 0;

                    return (
                        <div key={card.slug} style={s.cardTile} onClick={() => navigate(`/analytics/${card.slug}`)}>
                            <div style={s.cardTop}>
                                <div style={s.cardLeft}>
                                    {card.photoUrl
                                        ? <img src={card.photoUrl} alt="" style={s.avatar} />
                                        : <div style={s.avatarFallback}>{card.name?.[0] || "?"}</div>
                                    }
                                    <div>
                                        <div style={s.cardName}>{card.name}</div>
                                        <div style={s.cardSlug}>/{card.slug}</div>
                                    </div>
                                </div>
                                {todayViews > 0 && (
                                    <div style={s.todayBadge}>+{todayViews} today</div>
                                )}
                            </div>

                            {/* Mini sparkline */}
                            <div style={{ margin: "14px 0 16px" }}>
                                <MiniBar daily={a?.daily} />
                            </div>

                            {/* Stats row */}
                            <div style={s.statChips}>
                                <div style={s.chip}>
                                    <span style={{ color: TEAL }}>👁 {fmt(views)}</span>
                                    <span style={s.chipLabel}>Views</span>
                                </div>
                                <div style={s.chip}>
                                    <span style={{ color: PURPLE }}>💾 {fmt(saves)}</span>
                                    <span style={s.chipLabel}>Saves</span>
                                </div>
                                <div style={s.chip}>
                                    <span style={{ color: AMBER }}>📲 {fmt(wallet)}</span>
                                    <span style={s.chipLabel}>Wallet</span>
                                </div>
                                <div style={{ ...s.chip, marginLeft: "auto", background: "rgba(10,173,161,0.08)", border: "1px solid rgba(10,173,161,0.2)" }}>
                                    <span style={{ color: TEAL, fontSize: "11px", fontWeight: "700" }}>Details →</span>
                                </div>
                            </div>
                        </div>
                    );
                })}

                <p style={s.footer}>Data updates in real time · myprofilecorner.com</p>
            </div>
        </div>
    );
}

const s = {
    page: {
        minHeight: "100vh", background: "#080b10",
        display: "flex", flexDirection: "column",
        fontFamily: "'Outfit', 'Segoe UI', sans-serif",
    },
    topBar: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.05)",
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(8,11,16,0.92)", backdropFilter: "blur(12px)",
    },
    backBtn: {
        background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
        color: "#ccc", borderRadius: "10px", padding: "8px 16px",
        fontSize: "13px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
    },
    topLogo: { fontSize: "13px", fontWeight: "700", color: "rgba(255,255,255,0.3)" },

    body: { maxWidth: "560px", width: "100%", margin: "0 auto", padding: "0 20px 60px" },

    hero: { textAlign: "center", padding: "36px 0 28px" },
    heroChip: {
        display: "inline-block", background: "rgba(10,173,161,0.12)", color: TEAL,
        fontSize: "10px", fontWeight: "700", letterSpacing: "1.5px", textTransform: "uppercase",
        borderRadius: "100px", padding: "5px 14px", marginBottom: "12px",
    },
    heroName: { fontSize: "24px", fontWeight: "800", color: "#fff", margin: "0 0 6px", letterSpacing: "-0.5px" },
    heroSub: { fontSize: "13px", color: "rgba(255,255,255,0.3)", margin: 0 },

    totalRow: { display: "flex", gap: "10px", marginBottom: "28px" },
    totalTile: {
        flex: 1, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "16px", padding: "16px 12px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
    },
    totalIcon: { fontSize: "18px", marginBottom: "4px" },

    sectionLabel: {
        fontSize: "10px", fontWeight: "700", color: "rgba(255,255,255,0.3)",
        letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "12px",
    },

    cardTile: {
        background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "18px", padding: "18px", marginBottom: "12px", cursor: "pointer",
        transition: "border-color 0.15s",
    },
    cardTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    cardLeft: { display: "flex", alignItems: "center", gap: "12px" },
    avatar: { width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.1)" },
    avatarFallback: {
        width: "40px", height: "40px", borderRadius: "50%",
        background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "16px", fontWeight: "700", color: "#fff",
        border: "2px solid rgba(255,255,255,0.1)",
    },
    cardName: { fontSize: "15px", fontWeight: "700", color: "#fff" },
    cardSlug: { fontSize: "11px", color: "rgba(255,255,255,0.3)", marginTop: "2px" },
    todayBadge: {
        background: "rgba(10,173,161,0.12)", color: TEAL,
        fontSize: "10px", fontWeight: "700", borderRadius: "100px", padding: "4px 10px",
    },

    statChips: { display: "flex", gap: "8px", flexWrap: "wrap" },
    chip: {
        display: "flex", flexDirection: "column", alignItems: "center", gap: "2px",
        background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "10px", padding: "8px 14px", fontSize: "13px", fontWeight: "700",
    },
    chipLabel: { fontSize: "9px", color: "rgba(255,255,255,0.3)", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" },

    empty: { textAlign: "center", padding: "40px 20px" },
    footer: { textAlign: "center", fontSize: "11px", color: "rgba(255,255,255,0.15)", marginTop: "24px" },

    gate: {
        background: "rgba(255,255,255,0.04)", borderRadius: "20px",
        padding: "3rem 2rem", textAlign: "center", margin: "auto", maxWidth: "360px",
    },
    gateIcon: { fontSize: "2.5rem", marginBottom: "1rem" },
    gateTitle: { fontSize: "1.3rem", fontWeight: "700", color: "#fff", marginBottom: "1.5rem" },
    gateBtn: {
        padding: "0.75rem 2rem", background: TEAL, color: "#fff",
        border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "700",
        cursor: "pointer", marginTop: "16px",
    },
};
